import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';

/**
 * A modal detail view: a right-hand sidebar above 640px, a full-screen
 * sheet at 640px and below (phones). Closes on Esc, the backdrop and the close button;
 * focus is moved in, trapped while open and restored to the opener on
 * close; the page behind does not scroll while it is open. Nested dialogs
 * portaled out of the content (e.g. the step panel) keep their own Esc /
 * Tab handling: this one ignores events that start outside its own DOM.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  headerExtra,
  children,
  className,
  "data-testid": testId,
}: {
  open: boolean;
  onClose: () => void;
  /** Accessible name + visible heading. */
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Right of the title, left of the close button (status badge ...). */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  const uid = React.useId();
  const titleId = `${uid}-title`;
  const descId = `${uid}-desc`;
  const panel = React.useRef<HTMLDivElement>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Focus in, restore on close.
  React.useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus({ preventScroll: true });
    return () => {
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open]);

  // Lock the page behind the dialog (and keep its width: no layout jump).
  React.useEffect(() => {
    if (!open) return;
    const body = document.body;
    const oldOverflow = body.style.overflow;
    const oldPad = body.style.paddingRight;
    const bar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (bar > 0) body.style.paddingRight = `${bar}px`;
    return () => {
      body.style.overflow = oldOverflow;
      body.style.paddingRight = oldPad;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const el = panel.current;
    // Events bubbling out of a nested portaled dialog are its business.
    if (!el || !(e.target instanceof Node) || !el.contains(e.target)) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== "Tab") return;
    const items = Array.from(
      el.querySelectorAll<HTMLElement>(FOCUSABLE),
    ).filter((n) => n.offsetParent !== null);
    if (items.length === 0) {
      e.preventDefault();
      el.focus();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === el)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50" data-testid={testId}>
      <div
        className="fixed inset-0 bg-black/50 animate-in fade-in-0"
        onClick={onClose}
        aria-hidden
        data-testid="drawer-backdrop"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cn(
          "fixed inset-y-0 right-0 flex w-[min(48rem,100vw)] flex-col border-l bg-background text-foreground shadow-xl outline-none",
          "animate-in slide-in-from-right",
          // phones (<= 640px): a full-screen sheet instead of a sidebar
          "max-[640px]:inset-0 max-[640px]:w-auto max-[640px]:border-l-0 max-[640px]:slide-in-from-bottom",
          className,
        )}
      >
        <div className="flex items-start gap-2 border-b px-3 py-2.5 sm:px-4">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="truncate font-mono text-sm font-semibold"
            >
              {title}
            </h2>
            {description && (
              <p id={descId} className="text-xs text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {headerExtra}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close (Esc)"
            title="Close (Esc)"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 space-y-2.5 overflow-y-auto overscroll-contain p-3 sm:p-4">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
